/**
 * File d'attente Mode direct (Nicolas, 16/09).
 * La secrétaire prépare le patient et les documents ; le préleveur les prend.
 */

export const CHAMP_ATTENTE = "enAttenteDirect";

/*
 * UN PATIENT D'UN JOUR A VENIR EST « PLANIFIE » (25/09 17h18).
 *
 * La tablette 3.2 (86), publiee le 30/09 et qu'on ne reconstruit plus, lit la
 * file par `laboratoryId == X` et `enAttenteDirect == true`, et l'affiche
 * entiere : elle ne connait pas `dateRdv`. Un patient mis a demain y
 * apparaitrait des aujourd'hui.
 *
 * Un patient d'un jour a venir porte donc `attenteDirectPlanifiee: true` et
 * `enAttenteDirect: false` : la 3.2 ne le ramene pas, ce back-office et la
 * tablette suivante lisent les deux champs. Le jour venu, la tache du serveur
 * (POST /taches/file-directe-du-jour, juste apres minuit a Paris) le repasse a
 * `enAttenteDirect: true` ; cette page fait la meme correction a l'affichage,
 * si la tache n'est pas passee. Pris en charge : les deux a false, et on n'y
 * touche plus. Meme regle que backend/src/services/fileDirecteService.js.
 */
export const CHAMP_PLANIFIE = "attenteDirectPlanifiee";

/** Dans la file, du jour ou planifie. */
export function estEnAttenteDirect(appt) {
  return !!(appt && appt.modeDirect && (appt[CHAMP_ATTENTE] === true || appt[CHAMP_PLANIFIE] === true));
}

/** Les deux champs d'un patient en file, d'apres sa date de rendez-vous. */
export function etatFilePour(dateRdv, aujourdhui = jourLocal()) {
  const planifie = dateRdvValide(dateRdv) && String(dateRdv).trim() > aujourdhui;
  return { [CHAMP_ATTENTE]: !planifie, [CHAMP_PLANIFIE]: planifie };
}

/**
 * Ce qu'il faut ecrire pour remettre un patient en file dans le bon etat, ou
 * `null` (deja juste, ou plus en file : un patient pris en charge n'est
 * jamais remis dans la file).
 */
export function correctionFile(appt, aujourdhui = jourLocal()) {
  if (!appt || !(appt[CHAMP_ATTENTE] === true || appt[CHAMP_PLANIFIE] === true)) return null;
  const etat = etatFilePour(appt.dateRdv, aujourdhui);
  const juste =
    (appt[CHAMP_ATTENTE] === true) === etat[CHAMP_ATTENTE] &&
    (appt[CHAMP_PLANIFIE] === true) === etat[CHAMP_PLANIFIE];
  return juste ? null : etat;
}

/** Les deux lectures (du jour, planifies) en une file, sans doublon. */
export function fusionnerFiles(...listes) {
  const parId = new Map();
  for (const liste of listes) for (const p of liste || []) if (p?.id) parId.set(p.id, p);
  return [...parId.values()];
}

export function nomPatientFile(appt) {
  const usage = appt?.maritalName || appt?.lastName || "";
  const prenom = appt?.name || "";
  return `${usage} ${prenom}`.trim() || "Sans nom";
}

export function cheminStockageDirect(cle, nomFichier) {
  const jour = new Date().toISOString().slice(0, 10);
  const safe = String(nomFichier || "doc").replace(/[^A-Za-z0-9._-]+/g, "-");
  return `${jour}/direct-attente-${cle}-${Date.now()}-${safe}`;
}

/**
 * L'heure prevue du rendez-vous, telle qu'on l'affiche.
 *
 * Nicolas, 18/09 : « L'idee etant de gerer des patients avec RDV, rajoute un
 * champ pour l'heure prevue de RDV qui sera affichee sur la tablette. »
 *
 * On stocke « HH:MM », en clair : c'est une heure locale de laboratoire, elle
 * n'a pas de fuseau et ne sert qu'a ordonner la file et a la lire. Un
 * horodatage complet obligerait a choisir un jour, alors que la file est
 * remise a zero chaque matin.
 */
export function heureRdvValide(valeur) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(String(valeur || "").trim());
}

/** L'heure normalisee, ou une chaine vide si elle n'est pas lisible. */
export function normaliserHeureRdv(valeur) {
  const brut = String(valeur || "").trim().replace(/[hH.]/g, ":");
  const m = brut.match(/^(\d{1,2}):?(\d{2})$/);
  if (!m) return heureRdvValide(brut) ? brut : "";
  const h = String(Math.min(23, Number(m[1]))).padStart(2, "0");
  const min = String(Math.min(59, Number(m[2]))).padStart(2, "0");
  return `${h}:${min}`;
}

/*
 * LA DATE DU RENDEZ-VOUS (Nicolas, 25/09 14h06).
 *
 * « Dans la file d'attente que la secretaire peut creer avec les patients a
 * venir qui ont RDV, on peut aujourd'hui mettre l'heure, ce serait bien de
 * pouvoir choisir aussi la date. »
 *
 * On stocke `dateRdv` en « AAAA-MM-JJ », date locale du laboratoire, a cote
 * de `heureRdv` (« HH:MM ») : meme raison, pas de fuseau a choisir. Un patient
 * mis en file avant le 25/09 n'a pas de date : il est du jour de sa mise en
 * file (`created_at`). Sans date lisible du tout, il est du jour : on ne le
 * range jamais « nulle part ».
 *
 * La tablette 3.2 ne lit pas ce champ : elle montre toute la file qu'elle lit.
 * C'est pourquoi un patient d'un jour a venir n'y est pas (voir
 * `attenteDirectPlanifiee` plus haut). La suivante lit les deux, n'affiche que
 * le jour J et deroule les autres.
 */

const deux = (n) => String(n).padStart(2, "0");
const JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];

/** Le jour local « AAAA-MM-JJ » d'une date (aujourd'hui par defaut). */
export function jourLocal(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${deux(d.getMonth() + 1)}-${deux(d.getDate())}`;
}

/** Une date « AAAA-MM-JJ » qui existe au calendrier. */
export function dateRdvValide(valeur) {
  const m = String(valeur || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const [a, mo, j] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const d = new Date(Date.UTC(a, mo - 1, j));
  return d.getUTCFullYear() === a && d.getUTCMonth() === mo - 1 && d.getUTCDate() === j;
}

/** Le jour local d'un `created_at` Firestore (Timestamp, Date, texte ou nombre). */
export function jourDeCreation(createdAt) {
  if (!createdAt) return null;
  if (typeof createdAt?.toDate === "function") return jourLocal(createdAt.toDate());
  if (typeof createdAt?.seconds === "number") return jourLocal(new Date(createdAt.seconds * 1000));
  if (createdAt instanceof Date || typeof createdAt === "number" || typeof createdAt === "string") {
    return jourLocal(new Date(createdAt));
  }
  return null;
}

/**
 * Le jour prevu : la date de rendez-vous, sinon le jour de mise en file,
 * sinon `aujourdhui` (un patient qu'on vient d'ajouter n'a pas encore son
 * `created_at` serveur dans la premiere lecture).
 */
export function jourPrevu(patient, aujourdhui = jourLocal()) {
  if (dateRdvValide(patient?.dateRdv)) return String(patient.dateRdv).trim();
  return jourDeCreation(patient?.created_at) || aujourdhui;
}

function ecartEnJours(jour, aujourdhui) {
  const t = (s) => {
    const [a, m, j] = String(s).split("-").map(Number);
    return Date.UTC(a, m - 1, j);
  };
  return Math.round((t(jour) - t(aujourdhui)) / 86400000);
}

/** « Aujourd'hui », « Demain, samedi 26/09 », « Hier, jeudi 24/09 », « Mardi 29/09 ». */
export function libelleJour(jour, aujourdhui = jourLocal()) {
  if (!dateRdvValide(jour)) return "";
  const [a, m, j] = jour.split("-").map(Number);
  const nom = JOURS[new Date(Date.UTC(a, m - 1, j)).getUTCDay()];
  const annee = String(aujourdhui).slice(0, 4) === String(a) ? "" : `/${a}`;
  const date = `${nom} ${deux(j)}/${deux(m)}${annee}`;
  const e = ecartEnJours(jour, aujourdhui);
  if (e === 0) return "Aujourd'hui";
  if (e === 1) return `Demain, ${date}`;
  if (e === -1) return `Hier, ${date}`;
  return date.charAt(0).toUpperCase() + date.slice(1);
}

/**
 * La date saisie, controlee. Rend { date } ou { erreur }.
 *
 * Une date passee est refusee pour un NOUVEAU patient (une file sert a venir,
 * pas a hier) ; en modification, la date deja enregistree reste acceptee,
 * pour pouvoir corriger le reste d'une fiche sans deplacer le rendez-vous.
 */
export function controlerDateRdv(valeur, { aujourdhui = jourLocal(), dateEnregistree = null } = {}) {
  const v = String(valeur || "").trim();
  if (!v) return { date: aujourdhui };
  if (!dateRdvValide(v)) return { erreur: "La date du rendez-vous n'est pas une date valide." };
  if (v < aujourdhui && v !== dateEnregistree) {
    return { erreur: "La date du rendez-vous est déjà passée." };
  }
  return { date: v };
}

/**
 * La file rangee comme la secretaire l'attend : par jour, puis dans chaque
 * jour par heure de rendez-vous, les patients sans heure a la fin, du plus
 * recent au plus ancien.
 *
 * Un patient sans heure n'est pas « a 00:00 » : c'est quelqu'un qui passe
 * quand il peut. Le mettre en tete de file serait faux.
 */
export function rangerFile(patients = [], aujourdhui = jourLocal()) {
  const rang = (p) => (heureRdvValide(p?.heureRdv) ? 0 : 1);
  return [...patients].sort((a, b) => {
    const ja = jourPrevu(a, aujourdhui);
    const jb = jourPrevu(b, aujourdhui);
    if (ja !== jb) return ja < jb ? -1 : 1;
    if (rang(a) !== rang(b)) return rang(a) - rang(b);
    if (rang(a) === 0) return String(a.heureRdv).localeCompare(String(b.heureRdv));
    return (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0);
  });
}

/** La file rangee, coupee en jours : [{ jour, libelle, passe, patients }]. */
export function fileParJour(patients = [], aujourdhui = jourLocal()) {
  const groupes = [];
  for (const p of rangerFile(patients, aujourdhui)) {
    const jour = jourPrevu(p, aujourdhui);
    let g = groupes[groupes.length - 1];
    if (!g || g.jour !== jour) {
      g = { jour, libelle: libelleJour(jour, aujourdhui), passe: jour < aujourdhui, patients: [] };
      groupes.push(g);
    }
    g.patients.push(p);
  }
  return groupes;
}
